import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, ExternalLink, FileText, Check } from "lucide-react";
import { DecorPicker } from "./DecorPicker";
import { Swatch, fmtQty, fmtDate, domain, orderState } from "./helpers";

const GRID = "lg:grid lg:grid-cols-[34px_1.4fr_1.35fr_.9fr_76px_240px_30px] lg:gap-2.5 lg:items-center";

// Řádek položky (§6). Na mobilu se překlápí do karty (label nad hodnotou).
export function ItemRow({ item, decor, canEdit, decors, selected, onToggleSelect,
  onPickDecor, onPickCustom, onClearCustom, onQty, onOrder, onCancelOrder, onOpenDrawer, pickerOpen, setPickerOpen }) {
  const hasDecor = !!(item.dekor_id || item.dekor_text);
  const isCustom = !item.dekor_id && !!item.dekor_text;
  const bg = item.order ? "bg-success/10" : !hasDecor ? "bg-amber-500/5" : "bg-card";
  const CustomIcon = () => (
    <span className="w-[22px] h-[22px] rounded-[6px] bg-muted grid place-items-center text-muted-foreground shrink-0"><ExternalLink size={13} /></span>
  );

  return (
    <div className={`${GRID} px-3 py-3 border-b border-border last:border-b-0 ${bg} hover:bg-muted/50 transition-colors`}
      data-testid={`pasport-item-${item.id}`}>
      {/* checkbox */}
      <div className="hidden lg:flex justify-center">
        {canEdit && <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(item.id)} data-testid={`pasport-select-${item.id}`} />}
      </div>

      {/* CO SE ŘEŠÍ — tlačítko otevírá detail */}
      <button type="button" onClick={() => onOpenDrawer(item.id)} className="text-left min-w-0" data-testid={`pasport-item-open-${item.id}`}>
        <div className="font-semibold text-[15px] break-words">{item.label}</div>
        {(item.category || item.note) && (
          <div className="text-[12px] text-muted-foreground truncate">
            {[item.category, item.note].filter(Boolean).join(" · ")}
          </div>
        )}
      </button>

      {/* DEKOR / MODEL */}
      <div className="mt-2 lg:mt-0 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground lg:hidden mb-1">Dekor / model</div>
        {canEdit ? (
          <DecorPicker decors={decors} value={item.dekor_id} onPick={(id) => onPickDecor(item.id, id)}
            preferredCategory={item.category}
            onPickCustom={(c) => onPickCustom(item.id, c)}
            customCurrent={isCustom ? item.dekor_text : null}
            onClearCustom={isCustom ? () => onClearCustom(item.id) : undefined}
            open={pickerOpen} onOpenChange={(o) => setPickerOpen(o ? item.id : null)} testid={`pasport-decor-pop-${item.id}`}
            trigger={
              <button type="button" data-testid={`pasport-decor-btn-${item.id}`}
                className="w-full min-w-0 h-[38px] inline-flex items-center gap-2 border border-border rounded-[10px] bg-card px-2 text-sm hover:bg-secondary">
                {isCustom ? <CustomIcon /> : <Swatch decor={decor} size={22} />}
                <span className="flex-1 min-w-0 truncate text-left">{decor?.name || item.dekor_text || "vyber dekor"}</span>
                <ChevronDown size={14} className="opacity-70 shrink-0" />
              </button>
            } />
        ) : (
          <div className="flex items-center gap-2 text-sm">{isCustom ? <CustomIcon /> : <Swatch decor={decor} size={22} />}<span className="truncate">{decor?.name || item.dekor_text || "—"}</span></div>
        )}
      </div>

      {/* ODKAZ */}
      <div className="mt-2 lg:mt-0 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground lg:hidden mb-1">Odkaz</div>
        {decor ? (
          <>
            <a href={decor.url} target="_blank" rel="noreferrer"
              className="text-[13px] font-bold text-brand inline-flex items-center gap-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
              {domain(decor.url)} <ExternalLink size={12} className="shrink-0" />
            </a>
            <div className="text-[12px] text-muted-foreground truncate">{decor.supplier}</div>
          </>
        ) : item.dekor_url ? (
          <>
            <a href={item.dekor_url} target="_blank" rel="noreferrer"
              className="text-[13px] font-bold text-brand inline-flex items-center gap-1 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
              {domain(item.dekor_url)} <ExternalLink size={12} className="shrink-0" />
            </a>
            <div className="text-[12px] text-muted-foreground truncate">mimo vzorník</div>
          </>
        ) : <span className="text-sm text-muted-foreground">{item.dekor_text ? "mimo vzorník" : "—"}</span>}
      </div>

      {/* MNOŽSTVÍ */}
      <div className="mt-2 lg:mt-0">
        <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground lg:hidden mb-1">Množství</div>
        <QtyInput value={item.qty} disabled={!canEdit} onSave={(v) => onQty(item.id, v)} testid={`pasport-qty-${item.id}`} />
      </div>

      {/* OBJEDNÁNÍ */}
      <div className="mt-2 lg:mt-0 lg:text-right">
        {item.order?.order_id ? (
          <div className="lg:text-right" data-testid={`pasport-linked-${item.id}`}>
            <div className="text-[14px] font-bold text-success inline-flex items-center gap-1"><Check size={14} /> {orderState(item.order).title}</div>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-[12px] text-muted-foreground lg:justify-end mt-0.5">
              <span className="whitespace-nowrap">{orderState(item.order).sub}</span>
              {canEdit && <Link to={`/objednavky?o=${item.order.order_id}`} className="text-brand font-semibold hover:underline whitespace-nowrap"
                data-testid={`pasport-open-order-${item.id}`}>objednávka</Link>}
            </div>
          </div>
        ) : item.order ? (
          <div className="lg:text-right">
            <div className="text-[14px] font-bold text-success inline-flex items-center gap-1"><Check size={14} /> Objednáno {fmtDate(item.order.date)}</div>
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-[12px] text-muted-foreground lg:justify-end mt-0.5">
              <span className="whitespace-nowrap">č. obj. {item.order.no}</span>
              {item.order.url && (
                <a href={item.order.url} target="_blank" rel="noreferrer" title="Objednávka u dodavatele"
                  className="text-brand inline-flex items-center gap-0.5 whitespace-nowrap"><ExternalLink size={12} /> odkaz</a>
              )}
              {(item.order.files || []).length > 0 && (
                <span className="inline-flex items-center gap-0.5 whitespace-nowrap" title={item.order.files[0].name}><FileText size={12} /> {item.order.files.length}</span>
              )}
              {canEdit && (
                <button type="button" onClick={() => onCancelOrder(item)} className="text-brand hover:underline whitespace-nowrap" data-testid={`pasport-unorder-${item.id}`}>zrušit</button>
              )}
            </div>
          </div>
        ) : !hasDecor ? (
          <span className="text-[13px] font-bold text-amber-600 dark:text-amber-400">Chybí dekor / odkaz</span>
        ) : canEdit ? (
          <Button variant="outline" onClick={() => onOrder(item)} data-testid={`pasport-order-${item.id}`}
            className="h-9 w-full lg:w-auto border-brand text-brand bg-brand/15 hover:bg-brand/25 font-bold">Objednat</Button>
        ) : <span className="text-[13px] text-muted-foreground">neobjednáno</span>}
      </div>

      {/* chevron detail */}
      <button type="button" onClick={() => onOpenDrawer(item.id)} className="hidden lg:grid place-items-center text-muted-foreground hover:text-brand"
        data-testid={`pasport-item-chevron-${item.id}`}><ChevronRight size={18} /></button>
    </div>
  );
}

function QtyInput({ value, onSave, disabled, testid }) {
  const [v, setV] = useState(fmtQty(value));
  const t = useRef(null);
  useEffect(() => { setV(fmtQty(value)); }, [value]);
  const change = (e) => { setV(e.target.value); clearTimeout(t.current); t.current = setTimeout(() => onSave(e.target.value), 400); };
  const blur = () => { clearTimeout(t.current); onSave(v); };
  return <input value={v} onChange={change} onBlur={blur} disabled={disabled} inputMode="decimal" data-testid={testid}
    className="w-full lg:w-[76px] h-[38px] text-right bg-card border border-border rounded-[10px] px-2.5 text-[15px] font-semibold tabular-nums disabled:opacity-60" />;
}
